import { fork, takeEvery, cancel, call, select, put, all } from 'redux-saga/effects';
import routes from 'domain/router/routes';
import router from 'domain/router/router';
import initLocationChangeListener from 'domain/router/redux/locationChangeListener';
import { completeAction, noLocationChange } from 'domain/router/RouterActions';
import tokenKeeper from 'lib/apiTokenKeeper';
import { hasUserRequireDataSelector, getUserDataComplete } from 'domain/env';
import { locationIsChange, locationSelector } from 'domain/router/RouterSelector';
import { LOCATION_CHANGE, locationChangeAction } from 'domain/router/redux/reduxActions';
import { matchRoutes } from 'react-router-dom';
import { ToastEventHandler } from 'components/Tables/toast';

// State management
let activeTasks = [];

// Utility functions
const hasSaga = (route) => 'saga' in route && typeof route.saga === 'function';

const createCompleteCallback = (locationKey) =>
  function* unnamedCompleteCallback() {
    yield put(completeAction(locationKey));
  };

// Task management functions
function* cancelAllActiveTasks() {
  if (activeTasks.length > 0) {
    // Use array map with all() for parallel cancellation
    yield all(activeTasks.map((task) => cancel(task)));
    activeTasks = [];
    yield put(noLocationChange());
  }
}

function* executeSaga(route, params, location, dispatch, level) {
  // Inject params
  tokenKeeper.injectCurrentParams(params);
  // Load and execute saga
  const sagas = yield call(route.saga);
  const complete = createCompleteCallback(location.key);

  // Forked as an attached child of this wrapper task: cancelling the wrapper
  // (tracked in activeTasks) cancels this route saga and its watchers too.
  yield fork(sagas.default, location, { params }, complete, dispatch, route.sagasProps);
}

// Main processing function
function* processMatchedRoutes(matchedRoutes, location, dispatch) {
  let sagaFound = false;

  // Process routes recursively from level 1 (skip App wrapper)
  for (let level = 1; level < matchedRoutes.length; level++) {
    const { route, params } = matchedRoutes[level];

    if (hasSaga(route)) {
      sagaFound = true;
      // Register the wrapper task SYNCHRONOUSLY — before executeSaga awaits the
      // dynamic import() of the route saga chunk. Otherwise a second, rapid
      // navigation (e.g. a redirect that appends ?flowState=...) runs
      // cancelAllActiveTasks while activeTasks is still empty, fails to cancel
      // this in-flight load, and both navigations end up forking the same route
      // saga — registering its takeLatest watchers twice, so one dispatched
      // action triggers two HTTP requests.
      const task = yield fork(executeSaga, route, params, location, dispatch, level);
      activeTasks.push(task);
    }
  }

  return sagaFound;
}

// Navigation handler
function* handleNavigation({ payload, dispatch }) {
  ToastEventHandler.closeAll();

  const hasUserRequireData = yield select(hasUserRequireDataSelector);
  const isLocationChange = yield select(locationIsChange);
  const location = 'location' in payload ? payload.location : payload;

  if (!isLocationChange || !hasUserRequireData) {
    yield put(noLocationChange());
    return;
  }

  const matchedRoutes = matchRoutes(routes, location);
  if (!matchedRoutes) {
    yield put(noLocationChange());
    return;
  }

  // Cancel existing tasks and process new routes
  yield call(cancelAllActiveTasks);
  const sagaFound = yield call(processMatchedRoutes, matchedRoutes, location, dispatch);

  if (!sagaFound) {
    console.log('invoking complete as no sagas found in route hierarchy');
    yield put(completeAction(location.key));
  }
}

// Export functions
export function* afterInit(dispatch) {
  const payload = yield select(locationSelector);
  yield call(handleNavigation, { payload, dispatch });
}

export default function* navigator(dispatch) {
  const locationChangeListener = initLocationChangeListener(router, dispatch);

  yield takeEvery(LOCATION_CHANGE, ({ payload }) => handleNavigation({ payload, dispatch }));

  yield takeEvery(getUserDataComplete.type, () => afterInit(dispatch));
  yield put(locationChangeAction(router.state.location, router.state.historyAction));
  yield call(locationChangeListener);
}
